import type {
  LeagueSummary,
  DivisionPublic,
  LeaderboardEntryPublic,
  FirstPlaceSummaryPublic,
  RoundListPublic,
  RoundEpisodePage,
} from "@softmax/observatory-client/types";
import {
  ApiError,
  type ApiTransport,
} from "@softmax/observatory-client/transport";
import { featurableRoundStatusQuery } from "../featured-pool";
import { roundEpisodesPath } from "../round-episodes";
import {
  watchRoundLookback,
  roundEpisodePageSize,
  type DivisionReplays,
  type WatchLoadWave,
  type WatchLeagueState,
} from "./records";
import { activeRound, completedRounds } from "./rounds";
import { topTwo } from "./matchups";
import {
  deepeningPlan,
  deepenRoundPage,
  divisionReplaysFromPages,
} from "./round-pages";

import { watchStateFromDivisions } from "./watch-state";

async function loadDivisionFirstPlace({
  api,
  divisionId,
}: {
  api: Pick<ApiTransport, "get">;
  divisionId: string;
}): Promise<FirstPlaceSummaryPublic | null> {
  try {
    return await api.get<FirstPlaceSummaryPublic | null>(
      `/v2/divisions/${divisionId}/first-place`,
    );
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;

    // Optional reign metadata must not hide the stage when its endpoint fails.
    if (error.status !== 404 && error.status !== 401 && error.status !== 403) {
      console.warn(
        `/watch: the title reign is unavailable (${error.status}); the rail omits it`,
        error,
      );
    }

    return null;
  }
}

async function loadDivisionReplays({
  api,
  league,
  division,
  wave,
}: {
  api: Pick<ApiTransport, "get">;
  league: LeagueSummary;
  division: DivisionPublic;
  wave: WatchLoadWave;
}): Promise<DivisionReplays> {
  const [leaderboard, roundList, firstPlace] = await Promise.all([
    api.get<LeaderboardEntryPublic[] | null>(
      `/v2/divisions/${division.id}/leaderboard?include_recent_rounds=false`,
    ),
    api.get<RoundListPublic>(
      `/v2/rounds?division_id=${division.id}&${featurableRoundStatusQuery}&limit=${watchRoundLookback}`,
    ),
    loadDivisionFirstPlace({
      api,
      divisionId: division.id,
    }),
  ]);
  const entries = leaderboard ?? [];
  const active = activeRound(roundList.entries);
  const completed = completedRounds(roundList.entries);
  const walk = [...(active ? [active] : []), ...completed];
  const rounds = wave === "feature" ? walk.slice(0, 1) : walk;
  const firstPages = await Promise.all(
    rounds.map(async (round) => ({
      round,
      page: await api.get<RoundEpisodePage>(
        roundEpisodesPath({
          roundId: round.id,
          limit: roundEpisodePageSize,
        }),
      ),
    })),
  );
  const pair = topTwo(entries);
  const matchup = pair
    ? { divisionId: division.id, divisionName: division.name, ...pair }
    : null;
  const plan = matchup
    ? deepeningPlan({ roundPages: firstPages, matchup })
    : firstPages.map((entry) => ({ ...entry, extraPages: 0 }));
  const roundPages = await Promise.all(
    plan.map((entry) => deepenRoundPage({ api, ...entry })),
  );
  return divisionReplaysFromPages({
    league,
    division,
    leaderboard: entries,
    allRounds: roundList.entries,
    firstPlace,
    roundPages,
  });
}

export async function loadWatchLeagueState({
  api,
  league,
  wave,
}: {
  api: Pick<ApiTransport, "get">;
  league: LeagueSummary;
  wave: WatchLoadWave;
}): Promise<WatchLeagueState> {
  const divisions = await api.get<DivisionPublic[]>(
    `/v2/divisions?league_id=${league.id}&type=competition`,
  );
  const perDivision = await Promise.all(
    divisions.map((division) =>
      loadDivisionReplays({
        api,
        league,
        division,
        wave,
      }),
    ),
  );
  return watchStateFromDivisions({
    leagueId: league.id,
    divisions: perDivision,
  });
}
